import styled, { css } from 'styled-components';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router';
import * as Sentry from '@sentry/react';

import Button from 'react-bootstrap/Button';

import { getProducts } from '@utils/api/stripe';
import { useAuth } from '@contexts/User.context';

import { recurringIntervalSwitch } from '@utils/helpers';

/**
 * Validates that a Stripe product has the fields required to render a price.
 *
 * A product missing `currency` or `unit_amount` would otherwise crash the app
 * inside `Intl.NumberFormat` ("Currency code is required with currency style"),
 * taking down every page that lists products. Instead we skip the product and
 * fail loud in the logs + Sentry (error level triggers a team alert) so the
 * bad data is surfaced without ever showing a wrong/blank price to the user.
 *
 * @param {object} p - A product/price object from the /stripe/products response.
 * @returns {boolean} true if the product is safe to render.
 */
// Tracks already-reported malformed products so we don't flood the console /
// Sentry (and the resulting team alert) — isRenderableProduct runs on every
// render of the list and of each product button/tile.
const reportedMalformed = new Set();

const isRenderableProduct = p => {
	const missing = [];
	if (!p?.currency) missing.push('currency');
	if (p?.unit_amount == null) missing.push('unit_amount');

	if (missing.length === 0) return true;

	const key = `${p?.id ?? 'unknown'}:${missing.join(',')}`;
	if (!reportedMalformed.has(key)) {
		reportedMalformed.add(key);

		const message = `Skipping malformed Stripe product (id: ${
			p?.id ?? 'unknown'
		}, lookup_key: ${p?.lookup_key ?? 'unknown'}): missing ${missing.join(
			', '
		)}. Check the /stripe/products serialization on the backend.`;

		console.error(message, p);
		Sentry.captureMessage(message, 'error');
	}

	return false;
};

// Price ids already warned about, so a re-render doesn't repeat the warning.
const reportedUnnamed = new Set();

/**
 * Drops products without a display name in the Stripe product metadata, before
 * rendering. A missing name is catalogue config, not malformed price data, so it is a
 * `console.warn` (once per product), not a Sentry alert.
 *
 * Every list of `StripeProductButton` tiles must go through this first: the tiles
 * don't check the name themselves.
 *
 * @param {object[]} products Products/prices from the products or billing portal response
 * @returns {object[]} the products that have `product_metadata.name`
 */
export const filterNamedProducts = products =>
	products.filter(p => {
		if (p?.product_metadata?.name) return true;

		const key = p?.id ?? 'unknown';
		if (!reportedUnnamed.has(key)) {
			reportedUnnamed.add(key);
			console.warn(
				`Hiding Stripe product without a metadata name (product: ${
					p?.product?.id ?? p?.product ?? 'unknown'
				}, price: ${key}). Set "name" in the product metadata in Stripe.`
			);
		}
		return false;
	});

const commonProductTitleStyles = css`
	display: block;
	font-size: 1.5em;
	line-height: 1em;
	margin-bottom: 0.75em;
`;

export const StripeProductsButtonGroup = styled.div`
	width: 100%;
	display: grid;
	grid-template-columns: ${props => props.$columns || '1fr 1fr 1fr'};
	gap: 10px;

	.btn {
		background-color: ${p => p.theme.colors.backgroundAccentMuted};
		border: 1px solid ${p => p.theme.colors.green};
		border-radius: 3px;
		text-align: left;
		&:hover {
			background-color: ${p => p.theme.colors.green};
		}
		&[aria-disabled='true'] {
			background-color: ${p => p.theme.colors.backgroundAccentMuted};
			color: ${p => p.theme.colors.textAccent};
			opacity: 0.8;
			pointer-events: none;
			cursor: default;
		}
	}
`;

const BuyText = styled.span`
	font-size: 0.875em;
	color: ${p => p.theme.colors.textAccent};
	transition: color 0.2s ease-in-out;

	.btn:not([aria-disabled='true']):hover & {
		color: #ffffff;
	}
`;
const ChooseLabel = styled.span`
	font-weight: 600;
	color: ${p => p.theme.colors.green};
	transition: color 0.2s ease-in-out;

	.btn:not([aria-disabled='true']):hover & {
		color: #ffffff;
	}
`;
const ProductTile = styled.span`
	${commonProductTitleStyles}
`;
const ProductText = styled.span`
	display: block;
	font-size: 1rem;
	line-height: 1em;
	margin-bottom: 0.75em;
	color: ${p => p.theme.colors.textAccent};
	.btn:not([aria-disabled='true']):hover & {
		color: #ffffff;
	}
`;
const ProductDisabled = styled.span`
	display: block;
	font-size: 0.9;
	line-height: 1em;
	margin-bottom: 0.75em;
	color: ${p => p.theme.colors.textColor};
`;

const StripeProductsPresentGrid = styled.div`
	display: flex;
	justify-content: center;
	flex-wrap: wrap;
	gap: 2rem;
`;

const ProductPresent = styled.div`
	min-width: 300px;
	max-width: 100%;
	display: flex;
	flex-direction: column;
	justify-content: space-between;
	border: 1px solid ${p => p.theme.colors.backgroundAccent};
	border-radius: 0.375rem;
	padding: 1rem;
	color: ${p => p.theme.colors.textAccent};
`;

const ProductPresentTile = styled.span`
	${commonProductTitleStyles}
	color: ${props => props.theme.colors.green};
	margin-bottom: 1.5rem;
`;

const FeaturesList = styled.ul`
	padding: 0 0 0 1.2em;
	margin: 0 0 1rem;
	display: flex;
	flex-direction: column;
	gap: 0.25rem;
`;

/**
 *
 * Component for displaying available products from Stripe for purchase
 */
export default function StripeProducts({
	stripeLookupKeys = ['small_storage', 'medium_storage', 'large_storage'], // Default to small and medium storage
	type = 'button',
}) {
	const { currentUser } = useAuth();

	// Fetch products from Stripe
	const { data, isLoading, error } = useQuery({
		queryKey: [
			'stripe_products',
			stripeLookupKeys ?? stripeLookupKeys?.join('-'),
			currentUser?.uuid,
			currentUser?.id,
		],
		queryFn: () =>
			getProducts({
				lookup_keys: stripeLookupKeys,
				user_id: currentUser?.id,
			}),
		enabled: !!stripeLookupKeys,
	});

	const sortedProducts = data?.products
		? filterNamedProducts(data.products.filter(isRenderableProduct)).sort(
				(a, b) => a.unit_amount - b.unit_amount
			)
		: undefined;

	const renderProducts = (sortedProducts, type) => {
		if (type === 'button') {
			return (
				<StripeProductsButtonGroup aria-label="Available products" role="group">
					{sortedProducts.map(p => (
						<StripeProductButton key={p.id} p={p} />
					))}
				</StripeProductsButtonGroup>
			);
		} else {
			return (
				<StripeProductsPresentGrid>
					{sortedProducts.map(p => (
						<StripeProductPresent key={p.id} p={p} />
					))}
				</StripeProductsPresentGrid>
			);
		}
	};

	if (!stripeLookupKeys) return <div className="mt-3">Products could not be loaded...</div>;

	return (
		<section className="mt-3">
			{isLoading && <div>Loading products...</div>}

			{error && <div>We couldn't load plans right now. Please try again later.</div>}

			{!isLoading && !error && sortedProducts && (
				<>
					{sortedProducts?.length === 0 ? (
						<div>No products found</div>
					) : (
						renderProducts(sortedProducts, type)
					)}
				</>
			)}
		</section>
	);
}

export const StripeProductButton = ({ p, overwriteUrl = null, basic = false }) => {
	// Self-guard: this component is also rendered directly by callers
	// (e.g. SubscriptionsAvailableForUser) that don't pre-filter, so a
	// malformed product must not crash them either. Unnamed products are
	// filtered out by the caller (filterNamedProducts).
	if (!isRenderableProduct(p)) return null;

	const billedText =
		p.type === 'recurring' ? recurringIntervalSwitch(p?.recurring?.interval) : 'immediately';

	let url =
		overwriteUrl ??
		`/checkout?price_id=${p.id}&return_path=${encodeURIComponent(window.location.pathname)}`;
	if (!overwriteUrl && p.type === 'recurring') url = url + `&mode=subscription`;

	return (
		<Button
			as={Link}
			to={url}
			className="d-flex flex-column justify-content-between"
			disabled={p.custom_is_disabled}>
			<div>
				<BuyText>Billed {billedText}</BuyText>
				<ProductTile>{p.product_metadata?.name}</ProductTile>
				{!basic && p.features?.length > 0 && (
					<FeaturesList>
						{p.features?.map((f, i) => (
							<li key={`${p.id}-feature-${i}`}>
								<ProductText>{f.name}</ProductText>
							</li>
						))}
					</FeaturesList>
				)}
				{p.custom_is_disabled && (
					<ProductDisabled>{p.custom_disabled_reason}</ProductDisabled>
				)}
			</div>
			<span className="d-flex justify-content-between align-items-baseline gap-2">
				<span>
					{Intl.NumberFormat('en-IN', {
						style: 'currency',
						currency: p.currency,
					}).format(p.unit_amount / 100)}
				</span>
				<ChooseLabel>Choose plan</ChooseLabel>
			</span>
		</Button>
	);
};

const StripeProductPresent = ({ p }) => {
	if (!isRenderableProduct(p)) return null;

	const billedText =
		p.type === 'recurring' ? recurringIntervalSwitch(p?.recurring?.interval) : null;

	return (
		<ProductPresent>
			<div>
				{billedText && <BuyText>Billed {billedText}</BuyText>}
				<ProductPresentTile>{p.product_metadata?.name}</ProductPresentTile>
				{p.features?.length > 0 && (
					<FeaturesList>
						{p.features?.map((f, i) => (
							<li key={`${p.id}-feature-${i}`}>
								<ProductText>{f.name}</ProductText>
							</li>
						))}
					</FeaturesList>
				)}
			</div>
			<span>
				{Intl.NumberFormat('en-IN', {
					style: 'currency',
					currency: p.currency,
				}).format(p.unit_amount / 100)}
			</span>
		</ProductPresent>
	);
};
